import { useState, useEffect, useCallback } from 'react';
import { WorkOrderWithBuilding, WorkOrderStatus } from '../../types/workOrders';
import { workOrdersApi } from '../../api/workOrdersApi';

interface UseCompletedWorkOrdersReturn {
    workOrders: WorkOrderWithBuilding[];
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
}

/**
 * Hook to fetch completed work orders for a specific year.
 * Returns work orders with status === COMPLETED, sorted by completedAt descending (newest first).
 */
export const useCompletedWorkOrders = (year: number): UseCompletedWorkOrdersReturn => {
    const [workOrders, setWorkOrders] = useState<WorkOrderWithBuilding[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadCompletedWorkOrders = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const archived = await workOrdersApi.getArchiveByYear(year);

            // Filter to only COMPLETED status (not CANCELLED)
            const completed = archived
                .filter(wo => wo.status === WorkOrderStatus.COMPLETED)
                // Sort by completedAt descending (newest first)
                .sort((a, b) => (b.completedAt || '').localeCompare(a.completedAt || ''));

            setWorkOrders(completed);
        } catch (err) {
            console.error('Error loading completed work orders:', err);
            setError('Kunde inte ladda slutförda ärenden');
        } finally {
            setLoading(false);
        }
    }, [year]);

    // Load on mount or when year changes
    useEffect(() => {
        loadCompletedWorkOrders();
    }, [loadCompletedWorkOrders]);

    return {
        workOrders,
        loading,
        error,
        refresh: loadCompletedWorkOrders
    };
};
